/**
 * Attribution
 * -----------
 * Visual design ported from daisyUI's `.mockup-window` component.
 * Upstream: https://github.com/saadeghi/daisyui — MIT License,
 *   © 2020 Pouya Saadeghi (https://github.com/saadeghi).
 * The TypeScript wiring, attribute API, and ry-ui token mapping are
 * original to ry-ui; only the visual chrome (titlebar dots, padding
 * geometry, rounded clipping) is derived from the upstream CSS.
 *
 * @component ry-window-mockup
 * @category  display
 * @summary   Pure-visual chrome that frames arbitrary content as a desktop / macOS-style application window — title bar with traffic-light dots at top-left, optional title text, content slot below.
 *
 * Maps to daisyUI's `.mockup-window`. There is no behavior — the component is a
 * styled wrapper. Use it to frame screenshots, demos, code samples, or any UI
 * preview you want to read as "an app window" rather than a card.
 *
 * @attribute title    string?                       Optional centered title shown in the title bar. When omitted the bar holds only the traffic-light dots and content sits flush below.
 * @attribute variant  'default' | 'border'  default='default'  `default` renders a soft window (border + shadow + radius); `border` is the daisyUI-equivalent `mockup-window-border` look (heavier border, no shadow) suited for embedding inside other chrome.
 *
 * @content   Any HTML. The slot has no padding wrapper — author whatever inner
 *            layout fits the demo (an image, a paragraph, a nested ry-ui demo).
 *            For consistent inner padding, wrap the children in a `<ry-stack>`
 *            or your own padded container.
 *
 * @dom
 *   ry-window-mockup[title?][variant?]
 *     > [data-ry-target="chrome"] .ry-window-mockup__chrome   ← title bar (dots + optional title)
 *         > [data-ry-target="dots"] .ry-window-mockup__dots
 *         > [data-ry-target="title"] .ry-window-mockup__title  (only when [title] is set)
 *     > [data-ry-target="content"] .ry-window-mockup__content  ← slot wrapper
 *         > {children}
 *
 * @gotcha  The title bar is generated on `setup()` from the `title` attribute.
 *          Changing `title` after upgrade does NOT re-render the bar; set the
 *          attribute before insertion or replace the parent's `innerHTML` with
 *          a fresh `<ry-window-mockup>` block. This mirrors ry-card's setup-once
 *          children pattern.
 *
 * @gotcha  Authoring slot children with `<style>` or framework root elements is
 *          fine — the component does NOT touch their markup. Only the chrome
 *          row is generated; everything else is unmodified user content moved
 *          into `[data-ry-target="content"]` once during setup.
 *
 * @example basic
 * ```html
 * <ry-window-mockup>
 *   <p style="padding: 1.5rem; text-align: center;">Hello from the window!</p>
 * </ry-window-mockup>
 * ```
 *
 * @example with-title
 * ```html
 * <ry-window-mockup title="Untitled — ry-ui">
 *   <p style="padding: 1.5rem; text-align: center;">A window with a title bar.</p>
 * </ry-window-mockup>
 * ```
 *
 * @example border-variant
 * ```html
 * <ry-window-mockup title="Border variant" variant="border">
 *   <p style="padding: 1.5rem; text-align: center;">Heavier border, flatter look.</p>
 * </ry-window-mockup>
 * ```
 *
 * @tutorial renders-chrome-and-content
 * ```js
 * const el = mount('<ry-window-mockup><span id="kid">hi</span></ry-window-mockup>');
 * await expectEqual(!!el.querySelector('[data-ry-target="chrome"]'), true, 'chrome row rendered');
 * await expectEqual(!!el.querySelector('[data-ry-target="content"]'), true, 'content wrapper rendered');
 * await expectEqual(!!el.querySelector('#kid'), true, 'slotted child preserved');
 * ```
 *
 * @tutorial title-attribute-renders-text
 * ```js
 * const el = mount('<ry-window-mockup title="My App">x</ry-window-mockup>');
 * const t = el.querySelector('[data-ry-target="title"]');
 * await expectEqual(t?.textContent, 'My App', 'title slot shows attribute value');
 * ```
 *
 * @tutorial no-title-hides-title-slot
 * ```js
 * // Wrong assumption: the title slot always exists and is empty when no
 * // title is set. It does NOT — the slot is omitted entirely so the chrome
 * // row contains only the traffic-light dots.
 * const el = mount('<ry-window-mockup>x</ry-window-mockup>');
 * await expectEqual(el.querySelector('[data-ry-target="title"]'), null, 'no title slot when [title] absent');
 * await expectEqual(!!el.querySelector('[data-ry-target="dots"]'), true, 'dots always rendered');
 * ```
 *
 * @see ry-card
 */

import { RyElement } from '../core/ry-element.js';

export class RyWindowMockup extends RyElement {
  setup(): void {
    // Capture original children before we inject chrome.
    const children = Array.from(this.childNodes);

    // Build chrome row.
    const chrome = document.createElement('div');
    chrome.setAttribute('data-ry-target', 'chrome');
    chrome.className = 'ry-window-mockup__chrome';

    const dots = document.createElement('div');
    dots.setAttribute('data-ry-target', 'dots');
    dots.className = 'ry-window-mockup__dots';
    chrome.appendChild(dots);

    const titleText = this.getAttribute('title');
    if (titleText !== null && titleText !== '') {
      const title = document.createElement('div');
      title.setAttribute('data-ry-target', 'title');
      title.className = 'ry-window-mockup__title';
      title.textContent = titleText;
      chrome.appendChild(title);
      // Remove the host's title attribute so it doesn't surface as a native
      // tooltip on hover — the visual title in the chrome row replaces that.
      this.removeAttribute('title');
    }

    // Build content wrapper and re-parent the original children.
    const content = document.createElement('div');
    content.setAttribute('data-ry-target', 'content');
    content.className = 'ry-window-mockup__content';
    for (const node of children) content.appendChild(node);

    this.appendChild(chrome);
    this.appendChild(content);
  }
}

customElements.define('ry-window-mockup', RyWindowMockup);
